import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import Logo from "./Logo";
import { ArrowUpRight, Search, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

const STACK = [
  {
    image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?auto=format&fit=crop&w=900&q=80",
    label: "Dashboard · pptx",
  },
  {
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=900&q=80",
    label: "Vector pack · svg",
  },
  {
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=900&q=80",
    label: "Motion reel · mp4",
  },
];

const TICKER = ["powerpoint", "icons", "video", "stickers", "3d", "templates", "photos", "cv", "animation", "fonts"];

export default function HeroSection() {
  const { t } = useLang();
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % STACK.length), 4500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {/* subtle watermark logo */}
      <Logo
        size={720}
        color="hsl(var(--ink) / 0.035)"
        className="pointer-events-none absolute -start-40 -top-40 hidden lg:block"
      />
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 pb-16 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-20">
        {/* LEFT — editorial type */}
        <div className="lg:col-span-7 reveal">
          <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">
            <span className="inline-block h-2 w-2 rounded-full bg-accent animate-pulse" />
            {t("everythingFreeBadge")} — v1.0
          </div>

          <h1 className="font-display mt-7 text-[clamp(2.75rem,7vw,6.25rem)] leading-[0.95]">
            <span className="text-ink">Free media,</span>
            <br />
            <span className="font-serif text-accent">curated</span>
            <span className="text-ink"> by</span>
            <br />
            <span className="text-ink">creators, for</span>
            <br />
            <span className="text-ink">creators.</span>
            <span className="font-serif text-accent">.</span>
          </h1>

          <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-fg sm:text-lg">
            {t("heroSubtitle")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/browse" className="btn btn-primary group" data-testid="hero-browse-btn">
              {t("browseAssets")}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <Link to="/upload" className="btn btn-ghost" data-testid="hero-upload-btn">
              <Sparkles className="h-4 w-4" />
              {t("upload")}
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 text-xs uppercase tracking-[0.18em] text-muted-fg">
            <Stat label={t("noPayment")} />
            <Stat label={t("unlimited")} />
            <Stat label={t("commercial")} />
          </div>
        </div>

        {/* RIGHT — editorial card stack */}
        <div className="lg:col-span-5">
          <div className="relative mx-auto h-[420px] w-full max-w-md sm:h-[520px]">
            {STACK.map((s, idx) => {
              const offset = idx - i;
              const z = STACK.length - Math.abs(offset);
              const rot = offset * 4;
              const x = offset * 14;
              const y = Math.abs(offset) * 10;
              return (
                <div
                  key={idx}
                  className="absolute inset-0 transition-all duration-700 ease-out"
                  style={{
                    transform: `translate3d(${x}px, ${y}px, 0) rotate(${rot}deg)`,
                    zIndex: z,
                    opacity: idx === i ? 1 : 0.7,
                  }}
                >
                  <div
                    className="relative h-full w-full overflow-hidden rounded-3xl border"
                    style={{ borderColor: "hsl(var(--border))" }}
                  >
                    <img src={s.image} alt={s.label} className="h-full w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-5">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                        Featured · {String(idx + 1).padStart(2, "0")}/{String(STACK.length).padStart(2, "0")}
                      </div>
                      <div className="mt-1 font-display text-2xl text-white">{s.label}</div>
                    </div>
                    <span className="absolute end-4 top-4 chip chip-accent !bg-white !text-[hsl(var(--accent))] !border-white">
                      Free
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* MARQUEE divider */}
      <div className="border-y" style={{ borderColor: "hsl(var(--border))" }} data-testid="marquee-strip">
        <div className="marquee bg-[hsl(var(--ink))] py-4 text-[hsl(var(--bg))]">
          <div className="marquee-track">
            {[...TICKER, ...TICKER].map((w, idx) => (
              <span key={idx} className="font-display flex shrink-0 items-center gap-8 text-2xl uppercase tracking-tight sm:text-3xl">
                <span>{w}</span>
                <span className="font-serif text-accent">●</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1 w-6 bg-[hsl(var(--ink))]" />
      {label}
    </span>
  );
}
